/**
 * Sold-out rules, shared by every surface that can add to the cart.
 *
 * These live in one place because the storefront previously disagreed with
 * itself: ProductDetailClient blocked adding an out-of-stock product
 * (`inStock = stockQty > 0`, CTA reading "Currently out of stock"), while the
 * grid and rail cards rendered a live "+" for the very same product — 45 of
 * this store's 394 products were addable to the cart at quantity 0.
 */

/**
 * True only when the payload EXPLICITLY says there is nothing left.
 *
 * Deliberately fails open: `undefined` means "this response carried no
 * inventory figure", not "zero". Treating unknown as sold out would blank every
 * add button in the storefront the moment the API stopped sending the field —
 * a far worse failure than briefly allowing an add that checkout re-validates.
 */
export function isSoldOut(quantity?: number | null): boolean {
  return quantity !== undefined && quantity !== null && quantity <= 0;
}

/**
 * Card-level rule for the grid/rail product model.
 *
 * A product with variants stays purchasable while ANY variant still has units —
 * VariantPickerModal already disables the individual sold-out ones, so gating on
 * the parent quantity alone would hide options the buyer can actually purchase.
 * Everything else falls back to the product's own quantity.
 */
export function isProductSoldOut(p: {
  stock?: number;
  variantCount?: number;
  variants?: { stockQuantity?: number }[];
}): boolean {
  const variants = p.variants ?? [];
  // >= 2 is the same threshold the cards use to show the variant picker.
  if ((p.variantCount ?? 0) >= 2 && variants.length > 0) {
    return variants.every((v) => isSoldOut(v.stockQuantity));
  }
  return isSoldOut(p.stock);
}
